import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { usePositionContext } from './PositionContext';

export interface ChartPosition {
  id: string;
  symbol: string;
  type: 'Buy' | 'Sell';
  size: string;
  entry: string;
  current: string;
  pnl: string;
  pnlPercent: string;
  leverage: number;
  timestamp: number;
  isAI?: boolean;
  // Chart-specific properties
  chartX?: number;
  chartY?: number;
  slLevel?: number;
  tpLevel?: number;
  isVisible?: boolean;
  // Pending order properties
  isPendingOrder?: boolean;
  orderType?: 'market' | 'limit' | 'stop';
  stopPrice?: string;
}

interface ChartPositionContextType {
  chartPositions: ChartPosition[];
  updateChartPosition: (positionId: string, updates: Partial<ChartPosition>) => void;
  // `undefined` clears the level, which is how a target or stop is removed from the chart.
  setSLLevel: (positionId: string, slLevel: number | undefined) => void;
  setTPLevel: (positionId: string, tpLevel: number | undefined) => void;
  togglePositionVisibility: (positionId: string) => void;
  closeChartPosition: (positionId: string) => void;
  syncWithPositions: (symbol: string) => void;
  syncWithPendingOrders: (symbol: string) => void;
  // Drag and drop support
  movePosition: (positionId: string, newPrice: number) => void;
  isDragEnabled: (positionId: string, dragType: 'position' | 'sl' | 'tp') => boolean;
}

const ChartPositionContext = createContext<ChartPositionContextType | undefined>(undefined);

export const useChartPositionContext = () => {
  const context = useContext(ChartPositionContext);
  if (context === undefined) {
    throw new Error('useChartPositionContext must be used within a ChartPositionProvider');
  }
  return context;
};

interface ChartPositionProviderProps {
  children: ReactNode;
}

export const ChartPositionProvider: React.FC<ChartPositionProviderProps> = ({ children }) => {
  const [chartPositions, setChartPositions] = useState<ChartPosition[]>([]);
  const { openPositions, pendingOrders, closePosition, cancelOrder, setPositionExitLevels } =
    usePositionContext();

  // Sync chart positions with open positions for a specific symbol
  const syncWithPositions = useCallback((symbol: string) => {
    const symbolPositions = openPositions.filter(pos => pos.symbol === symbol);
    
    setChartPositions(prevPositions => {
      // Remove positions that are no longer in openPositions
      const filteredPositions = prevPositions.filter(chartPos => 
        symbolPositions.some(openPos => openPos.id === chartPos.id)
      );
      
      // Add new positions
      const newPositions = symbolPositions
        .filter(openPos => !filteredPositions.some(chartPos => chartPos.id === openPos.id))
        .map(openPos => ({
          ...openPos,
          chartX: 0,
          chartY: 0,
          // Read back from the position, which is the source of truth, so levels survive a
          // symbol switch. They used to be seeded with the entry price instead, which drew a
          // stop and a target line on top of the entry line for every position, so all three
          // overlapped and appeared stuck together.
          slLevel: openPos.stopLoss ? parseFloat(openPos.stopLoss) : undefined,
          tpLevel: openPos.takeProfit ? parseFloat(openPos.takeProfit) : undefined,
          isVisible: true,
        }));
      
      // Update existing positions with current data
      const updatedPositions = filteredPositions.map(chartPos => {
        const openPos = symbolPositions.find(op => op.id === chartPos.id);
        if (openPos) {
          return {
            ...chartPos,
            current: openPos.current,
            pnl: openPos.pnl,
            pnlPercent: openPos.pnlPercent,
            slLevel: openPos.stopLoss ? parseFloat(openPos.stopLoss) : undefined,
            tpLevel: openPos.takeProfit ? parseFloat(openPos.takeProfit) : undefined,
          };
        }
        return chartPos;
      });
      
      return [...updatedPositions, ...newPositions];
    });
  }, [openPositions]);

  // Sync chart positions with pending orders for a specific symbol
  const syncWithPendingOrders = useCallback((symbol: string) => {
    const symbolPendingOrders = pendingOrders.filter(order => order.symbol === symbol && order.status === 'Pending');
    
    setChartPositions(prevPositions => {
      // Remove pending orders that are no longer in pendingOrders
      const filteredPositions = prevPositions.filter(chartPos => 
        !chartPos.isPendingOrder || symbolPendingOrders.some(pendingOrder => pendingOrder.id === chartPos.id)
      );
      
      // Update existing pending orders and add new ones
      const updatedPositions = filteredPositions.map(chartPos => {
        if (!chartPos.isPendingOrder) return chartPos;
        
        const correspondingOrder = symbolPendingOrders.find(order => order.id === chartPos.id);
        if (correspondingOrder) {
          // For stop orders, use stopPrice for chart display; for limit orders, use price
          const displayPrice = correspondingOrder.orderType === 'stop' && correspondingOrder.stopPrice 
            ? correspondingOrder.stopPrice 
            : correspondingOrder.price;
          
          // Check if price has changed
          if (chartPos.entry !== displayPrice) {
          }
          
          // Update the chart position with the latest order data
          return {
            ...chartPos,
            entry: displayPrice,
            current: displayPrice,
            size: correspondingOrder.size,
            stopPrice: correspondingOrder.stopPrice,
          };
        }
        return chartPos;
      });
      
      // Add new pending orders
      const newPendingOrders = symbolPendingOrders
        .filter(pendingOrder => !updatedPositions.some(chartPos => chartPos.id === pendingOrder.id))
        .map(pendingOrder => {
          // For stop orders, use stopPrice for chart display; for limit orders, use price
          const displayPrice = pendingOrder.orderType === 'stop' && pendingOrder.stopPrice 
            ? pendingOrder.stopPrice 
            : pendingOrder.price;
            
          return {
            id: pendingOrder.id,
            symbol: pendingOrder.symbol,
            type: pendingOrder.type,
            size: pendingOrder.size,
            entry: displayPrice,
            current: displayPrice, // For pending orders, current = entry price
            pnl: '0.00', // No P&L for pending orders
            pnlPercent: '0.00%',
            leverage: pendingOrder.leverage,
            timestamp: pendingOrder.timestamp,
            chartX: 0,
            chartY: 0,
            slLevel: undefined, // Don't initialize SL/TP - let user set them
            tpLevel: undefined, // Don't initialize SL/TP - let user set them
            isVisible: true,
            isPendingOrder: true,
            orderType: pendingOrder.orderType,
            stopPrice: pendingOrder.stopPrice,
          };
        });
      
      return [...updatedPositions, ...newPendingOrders];
    });
  }, [pendingOrders]);

  // Update a specific chart position
  const updateChartPosition = useCallback((positionId: string, updates: Partial<ChartPosition>) => {
    setChartPositions(prevPositions =>
      prevPositions.map(pos =>
        pos.id === positionId ? { ...pos, ...updates } : pos
      )
    );
  }, []);

  // Set stop loss level
  //
  // Mirrored onto the position itself, not just the chart's copy. A level held only here would
  // be a drawing: nothing watches it, so it would never close anything.
  const setSLLevel = useCallback((positionId: string, slLevel: number | undefined) => {
    updateChartPosition(positionId, { slLevel });

    if (openPositions.some(position => position.id === positionId)) {
      setPositionExitLevels(positionId, {
        stopLoss: slLevel === undefined ? null : String(slLevel)
      });
    }
  }, [updateChartPosition, openPositions, setPositionExitLevels]);

  // Set take profit level
  const setTPLevel = useCallback((positionId: string, tpLevel: number | undefined) => {
    updateChartPosition(positionId, { tpLevel });

    if (openPositions.some(position => position.id === positionId)) {
      setPositionExitLevels(positionId, {
        takeProfit: tpLevel === undefined ? null : String(tpLevel)
      });
    }
  }, [updateChartPosition, openPositions, setPositionExitLevels]);

  // Toggle position visibility
  const togglePositionVisibility = useCallback((positionId: string) => {
    setChartPositions(prevPositions =>
      prevPositions.map(pos =>
        pos.id === positionId 
          ? { ...pos, isVisible: !pos.isVisible }
          : pos
      )
    );
  }, []);

  // Close a chart position
  const closeChartPosition = useCallback((positionId: string) => {
    const position = chartPositions.find(pos => pos.id === positionId);
    if (position) {
      if (position.isPendingOrder) {
        cancelOrder(positionId);
      } else {
        closePosition(positionId, position.current);
      }
    }
  }, [chartPositions, closePosition, cancelOrder]);

  // Move a position (for pending orders)
  const movePosition = useCallback((positionId: string, newPrice: number) => {
    updateChartPosition(positionId, { entry: newPrice.toString() });
  }, [updateChartPosition]);

  // Check if drag is enabled for a position and drag type
  const isDragEnabled = useCallback((positionId: string, dragType: 'position' | 'sl' | 'tp') => {
    const position = chartPositions.find(pos => pos.id === positionId);
    if (!position) return false;

    if (dragType === 'position') {
      // Only pending orders can have their whole position dragged
      return position.isPendingOrder === true;
    } else {
      // Both executed and pending orders can have SL/TP dragged
      return true;
    }
  }, [chartPositions]);

  const value: ChartPositionContextType = {
    chartPositions,
    updateChartPosition,
    setSLLevel,
    setTPLevel,
    togglePositionVisibility,
    closeChartPosition,
    syncWithPositions,
    syncWithPendingOrders,
    movePosition,
    isDragEnabled,
  };

  return (
    <ChartPositionContext.Provider value={value}>
      {children}
    </ChartPositionContext.Provider>
  );
};
